import {
  type AnchorHTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
} from 'react';

import type {
  _AriaButtonProps as AriaButtonProps,
  _ButtonAria as ButtonAria,
} from '@dynatrace/strato-components/buttons';
import {
  _mergeProps,
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';

import { useSegmentsTriggerContext } from './hooks/useTriggerContext.js';
import { isDev } from '../../core/utils/is-node-environment.js';

/**
 * Accepted properties for SegmentSelector customized trigger.
 * @public
 */
export type SegmentSelectorCustomTriggerProps = StylingProps &
  DataTestId & {
    /** Elements to be displayed in the CustomTrigger. */
    children:
      | ReactNode
      | ((
          customTriggerProps: {
            displayValue: ReactNode;
            isLoading: boolean;
          },
          props:
            | (StylingProps &
                DataTestId &
                (Partial<ButtonAria<AnchorHTMLAttributes<HTMLButtonElement>>> &
                  AriaButtonProps))
            | null,
        ) => ReactElement);
  };

/**
 * A trigger component to customize the SegmentSelector trigger.
 * It applies all necessary props on the first and closest child,
 * which, therefore, should be an interactive element.
 * @experimental
 */
export const CustomTrigger = (props: SegmentSelectorCustomTriggerProps) => {
  const { children, ...remainingProps } = props;
  const {
    triggerProps,
    displayValue,
    isLoading,
    buttonRef,
    showError,
    ...ariaProps
  } = useSegmentsTriggerContext();

  if (isValidElement<PropsWithChildren>(children)) {
    return cloneElement(children, {
      ..._mergeProps(
        { ref: buttonRef },
        triggerProps,
        remainingProps,
        ariaProps,
        children.props,
      ),
    });
  }

  if (typeof children === 'function') {
    return children(
      { displayValue, isLoading },
      _mergeProps({ ref: buttonRef }, ariaProps, triggerProps, remainingProps),
    );
  }

  if (isDev()) {
    // eslint-disable-next-line no-console
    console.warn(
      'When using the CustomTrigger component, you need to render valid children inside it.',
    );
  }

  return null;
};

CustomTrigger['displayName'] = 'SegmentSelector.CustomTrigger';
